import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Breadcrumb, Card, Col, Form, Row } from 'react-bootstrap';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import CustomToast from '../../components/CustomToast';
import '../../assets/css/org_hierarchy.css';

const MAX_LEVEL = 10;
const EXCLUDED_EMPLOYEE_TYPES = ['Ex_employee', 'Unverified', 'Client'];

const LEVEL_OPTIONS = Array.from({ length: MAX_LEVEL }, (_, i) => ({
  label: `L${i + 1}`,
  value: i + 1,
}));

const emptyForm = {
  title: '',
  user_id: null,
  parent_id: null,
  level: null,
};

const OrgHierarchy = () => {
  const { accessToken } = useAuth();
  const toastRef = useRef(null);

  const [tree, setTree] = useState([]);
  const [flat, setFlat] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // create | edit
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [levelOverridden, setLevelOverridden] = useState(false);

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  const showMessage = (severity, detail) => {
    const summary = severity.charAt(0).toUpperCase() + severity.slice(1);
    toastRef.current?.show(severity, summary, detail);
  };

  const fetchTree = useCallback(async () => {
    try {
      const response = await axios.get(`${config.apiBASEURL}/OrgHierarchyRoutes/tree`, {
        headers: authHeaders,
      });
      setTree(response.data.tree || []);
      setFlat(response.data.flat || []);
    } catch (error) {
      console.error(error);
      showMessage('error', error.response?.data?.message || 'Failed to load hierarchy');
    }
  }, [authHeaders]);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axios.get(`${config.apiBASEURL}/user-profile/getallusers`, {
        headers: authHeaders,
        params: { include_superadmin: true },
      });

      const mapped = (response.data || [])
        .filter((u) => !EXCLUDED_EMPLOYEE_TYPES.includes(u.user_type))
        .map((u) => ({
          user_id: u.user_id,
          first_name: u.first_name,
          last_name: u.last_name,
          email: u.email,
          Designation: u.Designation,
          user_type: u.user_type,
          profileImage: u.profileImage ? { image_url: u.profileImage.image_url } : null,
          label: `${u.first_name} ${u.last_name}${u.Designation ? ` — ${u.Designation}` : ''}`,
        }));

      setEmployees(mapped);
    } catch (error) {
      console.error(error);
      showMessage('error', error.response?.data?.message || 'Failed to load employees');
    }
  }, [authHeaders]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchTree(), fetchEmployees()]);
      setLoading(false);
    };
    if (accessToken) load();
  }, [accessToken, fetchTree, fetchEmployees]);

  const employeeOptions = useMemo(() => {
    // One person → one hierarchy node; keep current assignee available while editing
    const takenUserIds = new Set(
      flat
        .filter((n) => n.user_id != null && !(dialogMode === 'edit' && n.node_id === editingId))
        .map((n) => n.user_id)
    );
    const opts = employees
      .filter((e) => !takenUserIds.has(e.user_id))
      .map((e) => ({
        label: `${e.first_name} ${e.last_name}`.trim(),
        value: e.user_id,
        image_url: e.profileImage?.image_url || null,
      }));
    // Keep currently assigned person visible when editing (even if excluded from new picks)
    if (form.user_id && !opts.some((o) => o.value === form.user_id)) {
      const assignedNode = flat.find((n) => n.user_id === form.user_id);
      const assigned = assignedNode?.employee;
      if (assigned) {
        opts.unshift({
          label: `${assigned.first_name} ${assigned.last_name}`.trim(),
          value: form.user_id,
          image_url: assigned.profileImage?.image_url || null,
        });
      }
    }
    return opts;
  }, [employees, form.user_id, flat, dialogMode, editingId]);

  const employeeOptionTemplate = (option) => {
    if (!option) return null;
    const initials = (option.label || '?')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p.charAt(0).toUpperCase())
      .join('');
    return (
      <div className="org-dropdown-person">
        {option.image_url ? (
          <img className="org-dropdown-avatar" src={option.image_url} alt="" />
        ) : (
          <span className="org-dropdown-avatar is-initials">{initials}</span>
        )}
        <span className="org-dropdown-name">{option.label}</span>
      </div>
    );
  };

  const parentOptions = useMemo(() => {
    const exclude = new Set();
    if (dialogMode === 'edit' && editingId) {
      exclude.add(editingId);
      const stack = [editingId];
      while (stack.length) {
        const id = stack.pop();
        flat
          .filter((n) => n.parent_id === id)
          .forEach((child) => {
            exclude.add(child.node_id);
            stack.push(child.node_id);
          });
      }
    }

    return flat
      .filter((n) => !exclude.has(n.node_id))
      .map((n) => ({
        label: n.employee
          ? `L${n.level || 1} — ${n.title} (${n.employee.first_name} ${n.employee.last_name})`
          : `L${n.level || 1} — ${n.title}`,
        value: n.node_id,
      }));
  }, [flat, dialogMode, editingId]);

  const suggestedLevelForParent = useCallback((parentId) => {
    if (parentId == null) return 1;
    const parent = flat.find((n) => n.node_id === parentId);
    const parentLevel = parent?.level != null ? Number(parent.level) : 1;
    return Math.min(MAX_LEVEL, parentLevel + 1);
  }, [flat]);

  const levelOptionsForForm = useMemo(() => {
    if (form.parent_id == null) return LEVEL_OPTIONS;
    const parent = flat.find((n) => n.node_id === form.parent_id);
    const parentLevel = parent?.level != null ? Number(parent.level) : 1;
    return LEVEL_OPTIONS.filter((opt) => opt.value > parentLevel);
  }, [form.parent_id, flat]);

  const handleParentChange = (parentId) => {
    setForm((prev) => {
      const next = { ...prev, parent_id: parentId };
      if (parentId != null) {
        const parent = flat.find((n) => n.node_id === parentId);
        const parentLevel = parent?.level != null ? Number(parent.level) : 1;
        if (prev.level != null && prev.level <= parentLevel) {
          next.level = Math.min(MAX_LEVEL, parentLevel + 1);
          setLevelOverridden(false);
        }
      }
      return next;
    });
  };

  const openCreateRoot = () => {
    setDialogMode('create');
    setEditingId(null);
    setLevelOverridden(false);
    setForm({ ...emptyForm, parent_id: null, level: null });
    setDialogVisible(true);
  };

  const openAddChild = (parentId) => {
    setDialogMode('create');
    setEditingId(null);
    setSelectedId(parentId);
    setLevelOverridden(false);
    setForm({
      ...emptyForm,
      parent_id: parentId,
      level: null,
    });
    setDialogVisible(true);
  };

  const openEdit = (node) => {
    setDialogMode('edit');
    setEditingId(node.node_id);
    setSelectedId(node.node_id);
    setLevelOverridden(true);
    setForm({
      title: node.title || '',
      user_id: node.user_id ?? null,
      parent_id: node.parent_id ?? null,
      level: node.level != null ? Number(node.level) : 1,
    });
    setDialogVisible(true);
  };

  const handleSave = async () => {
    if (!form.title?.trim()) {
      showMessage('error', 'Please enter a title / role label');
      return;
    }
    if (!form.level || form.level < 1) {
      showMessage('error', 'Please select a level (L1, L2, …)');
      return;
    }
    if (form.parent_id != null) {
      const parent = flat.find((n) => n.node_id === form.parent_id);
      const parentLevel = parent?.level != null ? Number(parent.level) : 1;
      if (form.level <= parentLevel) {
        showMessage(
          'error',
          `Level must be higher than the parent's level (parent is L${parentLevel}). L${form.level} cannot report under L${parentLevel}.`
        );
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        user_id: form.user_id,
        parent_id: form.parent_id,
        level: form.level,
      };

      if (dialogMode === 'edit' && editingId) {
        await axios.put(
          `${config.apiBASEURL}/OrgHierarchyRoutes/nodes/${editingId}`,
          payload,
          { headers: authHeaders }
        );
        showMessage('success', 'Node updated');
      } else {
        await axios.post(
          `${config.apiBASEURL}/OrgHierarchyRoutes/nodes`,
          payload,
          { headers: authHeaders }
        );
        showMessage('success', 'Node added');
      }
      setDialogVisible(false);
      setForm(emptyForm);
      await fetchTree();
    } catch (error) {
      console.error(error);
      showMessage('error', error.response?.data?.message || 'Failed to save node');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (node) => {
    confirmDialog({
      message: `Delete "${node.title}" and all nodes under it?`,
      header: 'Delete hierarchy node',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await axios.delete(`${config.apiBASEURL}/OrgHierarchyRoutes/nodes/${node.node_id}`, {
            headers: authHeaders,
          });
          if (selectedId === node.node_id) setSelectedId(null);
          showMessage('success', 'Node deleted');
          await fetchTree();
        } catch (error) {
          console.error(error);
          showMessage('error', error.response?.data?.message || 'Failed to delete node');
        }
      },
    });
  };

  const initials = (employee, title) => {
    if (employee?.first_name || employee?.last_name) {
      return `${(employee.first_name || '').charAt(0)}${(employee.last_name || '').charAt(0)}`.toUpperCase();
    }
    return (title || '?').charAt(0).toUpperCase();
  };

  const renderNode = (node, parentLevel = null) => {
    const level = node.level != null ? Number(node.level) : 1;
    const skipSlots =
      parentLevel == null ? 0 : Math.max(0, level - Number(parentLevel) - 1);

    const isSelected = selectedId === node.node_id;
    const hasEmployee = Boolean(node.employee);
    const levelLabel = `L${level}`;

    const card = (
      <div
        className={`org-node-card${isSelected ? ' is-selected' : ''}`}
        onClick={() => setSelectedId(node.node_id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setSelectedId(node.node_id);
        }}
      >
        <div className="org-node-top">
          <div className="org-avatar">
            {node.employee?.profileImage?.image_url ? (
              <img src={node.employee.profileImage.image_url} alt="" />
            ) : (
              initials(node.employee, node.title)
            )}
          </div>
          <div className="org-node-meta">
            {hasEmployee ? (
              <>
                <p className="org-node-name">
                  {node.employee.first_name} {node.employee.last_name}
                </p>
                <p className="org-node-title">
                  {node.employee.Designation || node.employee.user_type}
                </p>
              </>
            ) : (
              <p className="org-node-role">{node.title || 'Unassigned slot'}</p>
            )}
            <span className="org-node-level" title={`Level ${levelLabel}`}>
              {levelLabel}
            </span>
          </div>
        </div>
        {!hasEmployee && <span className="org-node-badge">Open role</span>}

        <div className="org-node-actions">
          <Button
            icon="pi pi-plus"
            rounded
            text
            severity="success"
            tooltip="Add direct report"
            tooltipOptions={{ position: 'top' }}
            onClick={(e) => {
              e.stopPropagation();
              openAddChild(node.node_id);
            }}
          />
          <Button
            icon="pi pi-pencil"
            rounded
            text
            tooltip="Edit / reparent"
            tooltipOptions={{ position: 'top' }}
            onClick={(e) => {
              e.stopPropagation();
              openEdit(node);
            }}
          />
          <Button
            icon="pi pi-trash"
            rounded
            text
            severity="danger"
            tooltip="Delete"
            tooltipOptions={{ position: 'top' }}
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(node);
            }}
          />
        </div>
      </div>
    );

    const nodeBody = (
      <>
        {card}
        {node.children?.length > 0 && (
          <ul>{node.children.map((child) => renderNode(child, level))}</ul>
        )}
      </>
    );

    // Insert empty level slots so e.g. L3 under L1 sits on the L3 row
    let content = nodeBody;
    for (let s = 0; s < skipSlots; s += 1) {
      content = (
        <>
          <div className="org-skip-stem" aria-hidden="true" />
          <ul>
            <li className="org-skip-inner">{content}</li>
          </ul>
        </>
      );
    }

    return (
      <li
        key={node.node_id}
        className={skipSlots > 0 ? 'org-skip-level' : undefined}
      >
        {content}
      </li>
    );
  };

  const dialogTitle =
    dialogMode === 'edit'
      ? 'Edit node'
      : form.parent_id
        ? 'Add team / report'
        : 'Add root node';

  const suggestedLevel = suggestedLevelForParent(form.parent_id);
  return (
    <div className="org-hierarchy-page">
      <CustomToast ref={toastRef} />
      <ConfirmDialog />

      <Row className="body_content g-3">
        <Col xs={12}>
          <Breadcrumb>
            <Breadcrumb.Item>Employee</Breadcrumb.Item>
            <Breadcrumb.Item active>Organization Hierarchy</Breadcrumb.Item>
          </Breadcrumb>

          <div className="org-hierarchy-toolbar">
            <div>
              <h4>Organization Hierarchy</h4>
              <p>
                Build reporting lines with explicit levels (L1, L2, L3…). Level drives template assignment, even for skip-level reports.
              </p>
            </div>
            <div className="org-hierarchy-actions">
              <Button
                label="Add root"
                icon="pi pi-plus"
                onClick={openCreateRoot}
                disabled={loading}
              />
            </div>
          </div>

          <div className="org-legend">
            <span>Hover a card for add / edit / delete</span>
            <span className="skip">Skip-level reports sit on their level row — the line extends through empty levels</span>
          </div>

          <Card className="org-canvas-card mt-3">
            <div className="org-canvas-scroll">
              {loading ? (
                <div className="org-empty-state">
                  <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                  <p className="mt-3 mb-0">Loading hierarchy…</p>
                </div>
              ) : tree.length === 0 ? (
                <div className="org-empty-state">
                  <h5>No structure yet</h5>
                  <p>Start with a root (e.g. Founder at L1), then add reports with their own levels.</p>
                  <Button label="Create first node" icon="pi pi-plus" onClick={openCreateRoot} />
                </div>
              ) : (
                <div className="org-tree">
                  <ul>{tree.map((node) => renderNode(node))}</ul>
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <Dialog
        header={dialogTitle}
        visible={dialogVisible}
        style={{ width: 'min(480px, 94vw)' }}
        onHide={() => setDialogVisible(false)}
        footer={
          <div className="d-flex justify-content-end gap-2">
            <Button label="Cancel" text onClick={() => setDialogVisible(false)} disabled={saving} />
            <Button label={saving ? 'Saving…' : 'Save'} onClick={handleSave} loading={saving} />
          </div>
        }
      >
        <p className="org-dialog-hint">
          {dialogMode === 'edit'
            ? 'Change the label, level, assigned employee, or move this node under a different parent.'
            : form.parent_id
              ? 'Level defaults to parent + 1 (e.g. under L2 → L3). Override for skip-level roles.'
              : 'Set the root level (usually L1) when adding a top-level node.'}
        </p>

        <Form.Group className="mb-3">
          <Form.Label>Assign employee</Form.Label>
          <Dropdown
            value={form.user_id}
            options={employeeOptions}
            onChange={(e) => {
              const selectedEmployee = employees.find((emp) => emp.user_id === e.value);
              setForm((prev) => ({
                ...prev,
                user_id: e.value,
                title: selectedEmployee?.Designation || prev.title,
              }));
            }}
            placeholder="Select employee"
            filter
            showClear
            className="w-100"
            itemTemplate={employeeOptionTemplate}
            valueTemplate={(option, props) =>
              option ? employeeOptionTemplate(option) : <span>{props.placeholder}</span>
            }
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Title / role</Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g. Engineering Manager, Sales Team"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Level</Form.Label>
          <Dropdown
            value={form.level}
            options={levelOptionsForForm}
            onChange={(e) => {
              setLevelOverridden(true);
              setForm((prev) => ({ ...prev, level: e.value }));
            }}
            placeholder="Select level"
            className="w-100"
          />
          <Form.Text muted>
            {form.parent_id
              ? `Must be higher than parent (L${(flat.find((n) => n.node_id === form.parent_id)?.level || 1)}).`
              : 'Roots are usually L1 (founder / CEO).'}
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-1">
          <Form.Label>Reports to (parent)</Form.Label>
          <Dropdown
            value={form.parent_id}
            options={parentOptions}
            onChange={(e) => handleParentChange(e.value)}
            placeholder="Select parent (or leave empty for root)"
            filter
            showClear
            className="w-100"
          />
          <Form.Text muted>
            Leave the parent field empty if this is a new root / top-level node.
          </Form.Text>
        </Form.Group>
      </Dialog>
    </div>
  );
};

export default OrgHierarchy;
